import React, { useState, useEffect } from 'react';

const PrintingServices: React.FC<{ onNavigate: (page: string) => void }> = ({ onNavigate }) => {
  const images = [
    'Image_20251110204519_1_149.jpg',
    'Image_20251110204525_2_149.jpg',
    'Image_20251110204529_3_149.jpg',
    'Image_20251110204533_4_149.jpg',
    'Image_20251110204535_5_149.jpg',
    'Image_20251110204537_6_149.jpg',
    'Image_20251110204538_7_149.jpg',
    'Image_20251110204540_8_149.jpg',
    'image001.png',
    'image002.png',
    'image003.png',
    'image004.png'
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setItemsToShow(1);
      else if (window.innerWidth < 1024) setItemsToShow(2);
      else setItemsToShow(4);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % (images.length - (itemsToShow - 1)));
    }, 3000);
    return () => clearInterval(timer);
  }, [images.length, itemsToShow]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-slate-900 text-white pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block bg-blue-600 text-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-6">
            New Service
          </div>
          <h1 className="text-4xl md:text-7xl font-black mb-8 tracking-tight">
            Premium Metal <br/><span className="text-blue-500">3D Printing</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            In partnership with <span className="text-white font-bold">JAS Innovation Inc.</span>, we now offer BEGO-certified metal printing using advanced SLM technology for micron-level accuracy.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-16 pb-24">
        {/* Intro Card */}
        <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-xl border border-slate-100 p-6 md:p-12 mb-12">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-6">A Partnership for Precision</h2>
              <p className="text-slate-500 mb-6 leading-relaxed text-sm md:text-base">
                To expand our digital offerings, Piggy Dental has partnered with <strong>JAS Innovation Inc.</strong>, a leader in metal 3D printing. We chose JAS for their exclusive use of <strong>BEGO-certified Cobalt-Chromium (CoCr) alloys</strong> and Digital Precious Metals (DPM), ensuring every product meets our stringent standards for quality, biocompatibility, and long-term durability.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => onNavigate('contact')} className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all">
                  Get a Quote
                </button>
                <div className="px-8 py-4 rounded-2xl border border-slate-200 text-slate-500 font-bold text-xs uppercase tracking-widest text-center">
                  BEGO Certified
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-6 w-full">
               {/* Video Embed */}
               <div className="aspect-video w-full rounded-[2rem] overflow-hidden shadow-lg border border-slate-100 bg-black">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://www.youtube.com/embed/bpjmhrVEMfI?rel=0" 
                    title="JAS Innovation 3D Printing Process" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                  ></iframe>
               </div>
               <p className="text-center text-[10px] text-slate-400 font-medium">See the JAS Innovation process in action.</p>
            </div>
          </div>
        </div>

        {/* Technology & Value Props */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
                { title: 'SLM Technology', desc: 'Selective Laser Melting ensures dense, pore-free restorations with micron-level fit.', icon: '⚡' },
                { title: 'Cobalt-Chromium', desc: 'We use high-grade CoCr alloys known for superior strength and biocompatibility.', icon: '🛡️' },
                { title: 'Rapid Turnaround', desc: 'Streamlined digital workflows allow us to deliver most orders within 5 business days.', icon: '🚀' }
            ].map((feature, i) => (
                <div key={i} className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="text-4xl mb-4">{feature.icon}</div>
                    <h3 className="font-black text-slate-900 text-lg mb-2">{feature.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{feature.desc}</p>
                </div>
            ))}
        </div>

        {/* Pricing & Services */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Column 1: Ortho */}
          <div className="lg:col-span-2 bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 p-6 md:p-10">
            <h3 className="text-xl font-black text-slate-900 mb-8 uppercase tracking-wide flex items-center gap-3">
              <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-lg flex items-center justify-center text-sm">🦷</span>
              Orthodontic Bands
            </h3>
            <div className="space-y-6">
              {[
                { name: 'Single Band', price: '$40', break: 'Print: $20 | Polish: $20' },
                { name: 'Two with Bar', price: '$80', break: 'Print: $40 | Polish: $40' },
                { name: 'With Wings/Bars', price: '$130', break: 'Print: $70 | Polish: $60' },
                { name: 'RPE Frame', price: '$150', break: 'Print: $80 | Polish: $70' },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center border-b border-slate-50 pb-4 last:border-0">
                  <div>
                    <div className="font-bold text-slate-900 text-sm md:text-base">{item.name}</div>
                    <div className="text-[10px] md:text-xs font-mono text-slate-400 mt-1">{item.break}</div>
                  </div>
                  <div className="text-lg md:text-xl font-black text-blue-600">{item.price}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-yellow-50 text-yellow-800 p-4 rounded-xl text-[10px] md:text-xs font-bold border border-yellow-100">
              ⚠️ Important: Polishing services currently only available for Orthodontic Bands and RPE frames due to high demand.
            </div>
          </div>

          {/* Column 2: Other Services */}
          <div className="bg-slate-900 text-white rounded-[2rem] md:rounded-[2.5rem] shadow-xl p-6 md:p-10">
            <h3 className="text-xl font-black mb-8 uppercase tracking-wide flex items-center gap-3">
              <span className="bg-white/10 text-white w-8 h-8 rounded-lg flex items-center justify-center text-sm">⚙️</span>
              Printing Only
            </h3>
            <div className="space-y-6">
              {[
                { name: 'Implant Bars', price: '$90' },
                { name: 'Surgical Guides', price: '$90' },
                { name: 'RPD Frameworks', price: '$90' },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center border-b border-white/10 pb-4 last:border-0">
                  <div className="font-medium text-slate-300 text-sm md:text-base">{item.name}</div>
                  <div className="text-base md:text-lg font-black text-white">{item.price}</div>
                </div>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-white/10">
              <h4 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-4">Add-ons</h4>
              <div className="flex justify-between mb-2">
                <span className="text-xs md:text-sm text-slate-300">Sandblasting</span>
                <span className="font-mono font-bold">$20</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs md:text-sm text-slate-300">Support Removal</span>
                <span className="font-mono font-bold">$20</span>
              </div>
            </div>
          </div>
        </div>

        {/* Fancy Gallery Showcase */}
        <div className="mb-24 overflow-hidden">
          <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-8 text-center px-4">Precision in Every Detail</h3>
          <p className="text-slate-500 text-center max-w-2xl mx-auto mb-12 px-6 text-sm md:text-base">
            Explore our portfolio of high-precision metal printed restorations, showcasing the exceptional fit and finish achieved through our partnership with JAS Innovation Inc.
          </p>
          
          <div className="relative group px-4">
            <div 
              className="flex transition-transform duration-1000 ease-in-out" 
              style={{ transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)` }}
            >
              {images.map((img, i) => (
                <div key={i} className={`flex-shrink-0 px-2 ${itemsToShow === 1 ? 'w-full' : itemsToShow === 2 ? 'w-1/2' : 'w-1/4'}`}>
                  <div className="relative aspect-square overflow-hidden rounded-2xl bg-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group/item">
                    <img 
                      src={`/images/jas/${img}`} 
                      alt={`JAS 3D Printing Showcase ${i + 1}`} 
                      className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      <span className="text-white text-[10px] font-black uppercase tracking-widest">High Precision Detail</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center mt-8 gap-2">
              {Array.from({ length: images.length - (itemsToShow - 1) }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentIndex === i ? 'bg-blue-600 w-8' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-blue-600 text-white rounded-[2rem] md:rounded-[2.5rem] p-8 md:p-10">
            <h3 className="text-2xl font-black mb-6">Why JAS Innovation?</h3>
            <ul className="space-y-6">
              <li className="flex gap-4">
                <span className="text-2xl">⏱️</span>
                <div>
                  <div className="font-bold mb-1">Quick Turnaround</div>
                  <p className="text-blue-100 text-sm">Eliminate in-house delays. Standard orders in 5 business days.</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="text-2xl">💎</span>
                <div>
                  <div className="font-bold mb-1">Advanced Materials</div>
                  <p className="text-blue-100 text-sm">BEGO-certified CoCr and Digital Precious Metals (DPM).</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="text-2xl">🎨</span>
                <div>
                  <div className="font-bold mb-1">Design Services</div>
                  <p className="text-blue-100 text-sm">Outsource your design needs to our expert team.</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 p-8 md:p-10 flex flex-col justify-center text-center">
            <h3 className="text-xl font-black text-slate-900 mb-4">Shipping Information</h3>
            <p className="text-slate-500 mb-8 text-sm">All shipping costs are the responsibility of the customer. Rush orders available upon request.</p>
            <button onClick={() => onNavigate('contact')} className="w-full bg-slate-100 text-slate-900 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-colors">
              Contact for Design Services
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PrintingServices;
